import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users2, CheckCircle, GraduationCap, HeadphonesIcon, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Training = () => {
  const navigate = useNavigate();

  const trainingPrograms = [
    {
      icon: GraduationCap,
      title: 'Executive Briefings',
      description: 'Strategic overview sessions for leadership teams on food safety ROI and compliance management.',
      duration: '2-4 hours'
    },
    {
      icon: Users2,
      title: 'Manager Workshops',
      description: 'Hands-on training for managers on system administration, reporting, and team oversight.',
      duration: '1-2 days'
    },
    {
      icon: CheckCircle,
      title: 'Staff Training',
      description: 'Comprehensive training for frontline staff on checklist execution and mobile app usage.',
      duration: '4-8 hours'
    },
    {
      icon: HeadphonesIcon,
      title: '24/7 Support Portal',
      description: 'Always-available training resources, video tutorials, and technical support documentation.',
      duration: 'Ongoing'
    }
  ];

  const benefits = [
    'Reduce training time by 60% with structured programs',
    'Comprehensive video library with 200+ tutorials',
    'Role-based training paths for different user types',
    'Certification programs for food safety compliance',
    'Ongoing support with dedicated success managers'
  ];

  return (
    <div className="min-h-screen">
      <header className="glass-panel-nav">
        <div className="container mx-auto px-4 py-4">
          <Button 
            variant="glass" 
            onClick={() => navigate('/')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-crystal-orange/10 border border-crystal-orange/20">
              <Users2 className="h-8 w-8 text-crystal-orange" />
            </div>
            <div>
              <h1 className="heading-hero">Training & Support</h1>
              <p className="subheading mt-2">
                Comprehensive training programs to ensure successful adoption across teams
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="py-16">
        <div className="container mx-auto px-4">
          {/* Training Programs */}
          <section className="mb-16">
            <h2 className="heading-section mb-8 text-center">Training Programs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {trainingPrograms.map((program, index) => {
                const IconComponent = program.icon;
                return (
                  <Card key={program.title} className="glass-panel border-0 bg-transparent">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-crystal-orange/10 border border-crystal-orange/20">
                          <IconComponent className="h-6 w-6 text-crystal-orange" />
                        </div>
                        <div>
                          <CardTitle className="text-white">{program.title}</CardTitle>
                          <div className="text-crystal-orange text-sm font-medium">{program.duration}</div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-white/70">{program.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16">
            <div className="glass-panel-elevated p-8">
              <h2 className="heading-section mb-8 text-center">Training Benefits</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-crystal-orange mt-1 flex-shrink-0" />
                      <span className="text-white">{benefit}</span>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <div className="glass-panel p-8">
                    <div className="text-4xl font-bold text-crystal-orange mb-2">98%</div>
                    <p className="text-white/70 mb-4">User adoption rate</p>
                    <div className="text-2xl font-bold text-white mb-2">24/7</div>
                    <p className="text-white/70">Support availability</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <div className="glass-panel-elevated p-8">
              <h3 className="text-2xl font-bold text-white mb-4">
                Get Your Team Up to Speed Quickly
              </h3>
              <p className="text-white/70 mb-6 max-w-2xl mx-auto">
                Our comprehensive training programs ensure your entire team is confident and productive from day one.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="cta-primary px-8 py-3">
                  Schedule Training
                </Button>
                <Button className="cta-secondary px-8 py-3">
                  View Training Materials
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Training;